import React from "react";
import classNames from "classnames";

import setHelperText from "../helpers/setHelperText";
import { LoadingSkeleton } from "../../LoadingSkeleton";
import ReadOnlyContainer from "../../ReadOnlyContainer";
import { WrappedFieldInputProps, WrappedFieldMetaProps } from "redux-form";

export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  id: string;
  label: string;
  type: string;
  input: Partial<WrappedFieldInputProps>;
  textAreaExtraClasses?: string;
  helperText?: string;
  helperTextEnabled?: boolean;
  disabled?: boolean;
  showRequiredStar?: boolean;
  meta?: Partial<WrappedFieldMetaProps>;
  readOnly?: boolean;
}

export const TextArea: React.FC<TextAreaProps> = (props: TextAreaProps): JSX.Element => {
  const {
    id,
    label,
    type,
    helperText,
    helperTextEnabled = true,
    disabled,
    input,
    showRequiredStar,
    textAreaExtraClasses,
    meta = { touched: false, error: "", active: false },
    readOnly = false,
    ...rest
  } = props;
  const { touched, error, active } = meta;
  let { value } = input;

  const formElemRootClasses = classNames(
    "caprx-f-elem-root",
    { "caprx-f-elem-animate": active || value !== "" },
    { "caprx-f-elem-error": touched && error },
    { "caprx-f-elem-disabled": disabled },
    { "caprx-h-display-none": type === "hidden" },
  );

  // if the text area is active, show the border on the wrapper div
  const textAreaWrapperClasses = classNames(
    "caprx-f-elem-input",
    { "caprx-f-elem-textarea-wrapper": active },
    { "caprx-f-elem-read-only": readOnly },
  );

  const textAreaClasses = classNames("caprx-f-elem-textarea", textAreaExtraClasses);

  let body = <textarea id={id} disabled={disabled} className={textAreaClasses} {...input} {...rest} />;

  if (readOnly) {
    if (value === "" || value === undefined) {
      value = "N/A";
    }
    body = <ReadOnlyContainer {...rest} id={id} body={value} extraClasses={textAreaClasses} />;
  }

  return (
    <LoadingSkeleton>
      <div className={formElemRootClasses}>
        <div className={textAreaWrapperClasses}>
          {body}
          <label htmlFor={id} className="caprx-f-elem-label">
            {label}
            {showRequiredStar && <span className="required-star">*</span>}
          </label>
        </div>
        {helperTextEnabled && <div className="caprx-f-elem-helper-text">{setHelperText(helperText, touched, error)}</div>}
      </div>
    </LoadingSkeleton>
  );
};

export default TextArea;
